export const buildFilterQueryParams = (
  filters = [],
  filterFns = {}
) => {
  const params = {};

  const operatorMap = {
    eq: "eq",
    equals: "eq",
    equal: "eq",
    "==": "eq",
    "===": "eq",

    neq: "neq",
    ne: "neq",
    not: "neq",
    notequals: "neq",
    notEquals: "neq",
    "!=": "neq",

    like: "like",
    fuzzy: "like",
    contains: "like",
    startswith: "like",
    startsWith: "like",
    endswith: "like",
    endsWith: "like",

    gt: "gt",
    greaterthan: "gt",
    greaterThan: "gt",

    gte: "gte",
    ge: "gte",
    greaterthanorequal: "gte",
    greaterThanOrEqualTo: "gte",

    lt: "lt",
    lessthan: "lt",
    lessThan: "lt",

    lte: "lte",
    le: "lte",
    lessthanorequal: "lte",
    lessThanOrEqualTo: "lte",

    between: "between",

    null: "null",
    isnull: "null",
    isNull: "null",
    isempty: "null",
    isEmpty: "null",

    notnull: "notnull",
    isnotnull: "notnull",
    isNotNull: "notnull",
    isNotEmpty: "notnull",
    isnotempty: "notnull",
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return value;
    if (value instanceof Date) {
      // Format date as YYYY-MM-DD for date inputs
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          item === null || item === undefined ? "" : item.toString()
        )
        .join(",");
    }
    if (typeof value === "object") {
      if ("value" in value) {
        return value.value;
      }
      if (
        ("from" in value || "start" in value) &&
        ("to" in value || "end" in value)
      ) {
        const start = value.from ?? value.start ?? "";
        const end = value.to ?? value.end ?? "";
        return `${start},${end}`;
      }
      return JSON.stringify(value);
    }
    // Ensure date strings are in YYYY-MM-DD format
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.split("T")[0]; // Extract date part if it's a datetime string
    }
    return value;
  };

  filters.forEach((filter) => {
    if (!filter?.id) return;

    const rawOperator =
      filter.operator || filter.filterFn || filterFns?.[filter.id] || "eq";
    const normalizedRawOperator = String(rawOperator).trim();
    const operatorKey = operatorMap[normalizedRawOperator] || "eq";

    if (operatorKey === "between") {
      let formatted = filter.value;
      if (!Array.isArray(formatted)) {
        formatted = formatValue(formatted)?.split(",");
      }
      if (!Array.isArray(formatted) || formatted.length < 2) return;

      const [start, end] = formatted;
      if (
        start === undefined ||
        start === "" ||
        end === undefined ||
        end === ""
      ) {
        return;
      }

      params[`${filter.id}_between`] = `${start},${end}`;
      return;
    }

    if (operatorKey === "null" || operatorKey === "notnull") {
      let boolValue = true;

      if (typeof filter.value === "boolean") {
        boolValue = filter.value;
      } else if (typeof filter.value === "string") {
        const normalized = filter.value.trim().toLowerCase();
        if (normalized === "false") {
          boolValue = false;
        } else if (normalized === "true") {
          boolValue = true;
        }
      }

      params[`${filter.id}_${operatorKey}`] = boolValue;
      return;
    }

    const formattedValue = formatValue(filter.value);
    
    // Check if this is a date value - be more flexible with date detection
    const isDateValue = 
      formattedValue instanceof Date ||
      (typeof formattedValue === "string" && /^\d{4}-\d{2}-\d{2}/.test(formattedValue)) ||
      (typeof filter.value === "string" && /^\d{4}-\d{2}-\d{2}/.test(filter.value));
    
    const isEmptyValue =
      formattedValue === undefined ||
      formattedValue === null ||
      (typeof formattedValue === "string" &&
        formattedValue.trim().length === 0);

    // For date values, don't skip empty check - let it through if it's a valid date format
    // But still skip if it's truly empty
    if (isEmptyValue && !isDateValue) {
      if (operatorKey === "eq") {
        return;
      }
      if (operatorKey === "like") {
        return;
      }
    }
    
    // If it's a date value but formattedValue is empty, use the original filter.value
    const finalValue = (isEmptyValue && isDateValue && filter.value) 
      ? formatValue(filter.value) 
      : formattedValue;
    
    // Final check - if still empty after formatting, skip
    if (!finalValue || (typeof finalValue === "string" && finalValue.trim().length === 0)) {
      if (operatorKey === "eq") {
        return;
      }
    }
    // For date values, always use _eq suffix for equals operator to match backend expectations
    const finalIsDateValue = typeof finalValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(finalValue);
    const paramKey =
      operatorKey === "eq" && finalIsDateValue
        ? `${filter.id}_eq`
        : operatorKey === "eq"
        ? filter.id
        : `${filter.id}_${operatorKey}`;

    if (operatorKey === "like") {
      const rawLower = normalizedRawOperator.toLowerCase();
      let stringValue = finalValue?.toString() ?? "";

      const hasPercent =
        typeof stringValue === "string" &&
        (stringValue.includes("%") || stringValue.includes("*"));

      if (!hasPercent) {
        if (rawLower.includes("starts")) {
          stringValue = `${stringValue}%`;
        } else if (rawLower.includes("ends")) {
          stringValue = `%${stringValue}`;
        } else {
          stringValue = `%${stringValue}%`;
        }
      }

      params[paramKey] = stringValue;
      return;
    }

    params[paramKey] = finalValue;
  });

  return params;
};

